"use client";

import { Button, Text } from "@pamperedchef/design-system";
import { FeaturedText, RichText } from "@/composites/foundation";
import type { Cta, CmsImage } from "@/composites/landing/types";
import { CalloutBannerDecoration } from "./CalloutBannerDecoration";
import * as styles from "./CalloutBanner.module.scss";

export interface CalloutBannerProps {
	eyebrow?: string;
	headline: string;
	body?: string;
	primaryCta?: Cta;
	secondaryCta?: Cta;
	backgroundImage?: CmsImage;
	alignment?: "left" | "center";
}

/**
 * Closing call-to-action banner — a teal-framed band with a centered teal
 * serif headline, supporting copy, and up to two CTAs (a yellow filled primary
 * and an outlined secondary). Reusable anywhere a "don't miss this" prompt is
 * needed, not just at the page foot. Presentational, props-in, RSC-safe (CTAs
 * are `href`s, navigated via `onPress`).
 */
export function CalloutBanner({
	eyebrow,
	headline,
	body,
	primaryCta,
	secondaryCta,
	backgroundImage,
	alignment = "center"
}: CalloutBannerProps) {
	return (
		<section
			className={styles["callout-banner"]}
			data-component="CalloutBanner"
			data-alignment={alignment}
		>
			<CalloutBannerDecoration
				className={styles["callout-banner__decoration"]}
			/>

			{backgroundImage ? (
				<div
					className={styles["callout-banner__media"]}
					aria-hidden="true"
				>
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img
						src={backgroundImage.url}
						alt={backgroundImage.alt ?? ""}
						className={styles["callout-banner__image"]}
					/>
				</div>
			) : null}

			<div className={styles["callout-banner__content"]}>
				{eyebrow ? (
					<Text
						tag="p"
						styledAs="xsb"
						className={styles["callout-banner__eyebrow"]}
					>
						{eyebrow}
					</Text>
				) : null}

				<FeaturedText
					className={styles["callout-banner__headline"]}
					headline={headline}
					styledAs="display-md"
					alignment="center"
					headingTag="h2"
				/>

				{body ? (
					<RichText
						content={body}
						className={styles["callout-banner__body"]}
					/>
				) : null}

				{primaryCta?.title || secondaryCta?.title ? (
					<div className={styles["callout-banner__actions"]}>
						{primaryCta?.title ? (
							<Button
								kind="primary"
								size="lg"
								onPress={() => {
									window.location.href = primaryCta.href;
								}}
							>
								{primaryCta.title}
							</Button>
						) : null}
						{secondaryCta?.title ? (
							<Button
								kind="secondary"
								size="lg"
								onPress={() => {
									window.location.href = secondaryCta.href;
								}}
							>
								{secondaryCta.title}
							</Button>
						) : null}
					</div>
				) : null}
			</div>
		</section>
	);
}
